import React from "react";
import MuiTabs from "@mui/material/Tabs";

import InfoTooltip from "../InfoTooltip";
import { useStyles } from "./Tabs.styles";
import { OnChange, TabsProps } from "./Tabs.types";
import makeStyles from "@mui/styles/makeStyles";

export const useTabsStyles = makeStyles((theme) => ({
  root: (props: TabsProps) => ({
    position: "relative",
    paddingLeft: theme.spacing(props.sidePadding || 0),
    paddingRight: theme.spacing(props.sidePadding || 0),
    minHeight: 36,
    "&::before": {
      content: "''",
      position: "absolute",
      right: 0,
      bottom: 0,
      left: 0,
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
    },
    [theme.breakpoints.up("md")]: {
      minHeight: 30,
    },
  }),
  indicator: {
    height: 1,
  },
}));

// Rendered as a child of MuiTabs, which clones every child with Tab-only props
// (fullWidth, textColor, selectionFollowsFocus, indicator…). A plain <div> child
// would forward those to the DOM (React "unrecognized prop" warnings), so this
// wrapper consumes only what it needs and drops the injected Tab props.
function TabsInfo({
  info,
  infoBgColor,
  className,
}: {
  info: React.ReactNode;
  infoBgColor?: "grey" | "white";
  className?: string;
}) {
  return (
    <div className={className}>
      <InfoTooltip title={info} size="small" bgColor={infoBgColor} />
    </div>
  );
}

function Tabs(props: TabsProps) {
  const { info, infoBgColor, onChange, children, sidePadding, ...other } =
    props;

  const tabClasses = useTabsStyles(props);

  const classes = useStyles();

  const handleChange: OnChange = (...args: any[]) => {
    if (onChange) {
      onChange(args[1]);
    }
  };

  return (
    <MuiTabs
      {...other}
      classes={tabClasses}
      indicatorColor="primary"
      onChange={handleChange}
    >
      {children}
      {info && (
        <TabsInfo
          info={info}
          infoBgColor={infoBgColor}
          className={classes.info}
        />
      )}
    </MuiTabs>
  );
}

export default Tabs;
